/**
 * PerDishConsumptionChart Component
 * Line chart with toggle functionality to show per-dish, total, or both consumption values
 */

import { formatNumberEs } from '../../utils/formatters';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceDot,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card } from './Card';
import { LoadingSpinner } from './LoadingSpinner';

import type {
  PerDishMetrics,
  DisplayMode,
} from '../../utils/perDishCalculations';
import { useStableChartHeight } from '../../hooks/useStableChartHeight';
import { WATER_UNIT } from '../../utils/formatters';
import {
  type GoalChartPlan,
  type GoalMilestone,
} from '../../features/goals/goalChartProjection';

// ============================================================================
// Types
// ============================================================================

export interface PerDishConsumptionChartProps {
  data: PerDishMetrics[];
  consumptionType:
    | 'kwh'
    | 'm3_water'
    | 'm3_gas'
    | 'kg_recyclables'
    | 'kg_organic_waste'
    | 'kg_general_waste'
    | 'kg_co2e';
  title: string;
  loading?: boolean;
  isRefetching?: boolean;
  error?: string | undefined;
  className?: string;
  goalPlan?: GoalChartPlan | null;
}

type ConsumptionType = PerDishConsumptionChartProps['consumptionType'];
type PerDishValueKey =
  | 'kwh_per_dish'
  | 'm3_water_per_dish'
  | 'm3_gas_per_dish'
  | 'kg_recyclables_per_dish'
  | 'kg_organic_waste_per_dish'
  | 'kg_general_waste_per_dish'
  | 'kg_co2e_per_dish';
type TotalValueKey =
  | 'kwh_total'
  | 'm3_water_total'
  | 'm3_gas_total'
  | 'kg_recyclables_total'
  | 'kg_organic_waste_total'
  | 'kg_general_waste_total'
  | 'kg_co2e_total';

const PER_DISH_KEYS: Record<ConsumptionType, PerDishValueKey> = {
  kwh: 'kwh_per_dish',
  m3_water: 'm3_water_per_dish',
  m3_gas: 'm3_gas_per_dish',
  kg_recyclables: 'kg_recyclables_per_dish',
  kg_organic_waste: 'kg_organic_waste_per_dish',
  kg_general_waste: 'kg_general_waste_per_dish',
  kg_co2e: 'kg_co2e_per_dish',
};

const TOTAL_KEYS: Record<ConsumptionType, TotalValueKey> = {
  kwh: 'kwh_total',
  m3_water: 'm3_water_total',
  m3_gas: 'm3_gas_total',
  kg_recyclables: 'kg_recyclables_total',
  kg_organic_waste: 'kg_organic_waste_total',
  kg_general_waste: 'kg_general_waste_total',
  kg_co2e: 'kg_co2e_total',
};

interface ChartDisplayPoint {
  date: string;
  value: number | null;
  formatted_date: string;
  total_value?: number | null;
  per_dish_value?: number | null;
  covers_count?: number | null;
  has_sales_data?: boolean;
  [key: string]: string | number | boolean | null | undefined;
}

// ============================================================================
// Styled Components
// ============================================================================

const ChartContainer = styled(Card)<{ $minHeight: number }>`
  padding: 1.5rem;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: ${({ $minHeight }) => `${$minHeight}px`};
  transition: min-height 0.3s ease-in-out;

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: ${({ $minHeight }) => `${Math.max($minHeight - 40, 250)}px`};
  }
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);

  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--surface);

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${(props) =>
    props.$isActive ? 'var(--action-primary)' : 'var(--surface)'};
  color: ${(props) => (props.$isActive ? 'white' : 'var(--text-secondary)')};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 40px;

  &:hover {
    background: ${(props) =>
      props.$isActive
        ? 'var(--action-primary-hover)'
        : 'var(--interactive-hover)'};
    color: ${(props) => (props.$isActive ? 'white' : 'var(--text-primary)')};
  }

  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: -3px;
  }

  @media (max-width: 768px) {
    flex: 1;
    min-height: 44px;
    padding: 0.6rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const ChartContent = styled.div<{ $minHeight: number }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ $minHeight }) => `${$minHeight}px`};
  transition: all 0.3s ease-in-out;

  @media (max-width: 768px) {
    min-height: ${({ $minHeight }) => `${Math.max($minHeight - 20, 180)}px`};
  }
`;

const ErrorMessage = styled.div`
  color: var(--danger-600);
  text-align: center;
  font-size: 0.9rem;
  padding: 1rem;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  text-align: center;
  font-size: 0.9rem;
  padding: 1rem;
`;

const UnitLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const GoalVisibilityHint = styled.div`
  margin-top: 0.25rem;
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

// ============================================================================
// Custom Tooltip Component
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value?: number;
    payload: ChartDisplayPoint;
    dataKey?: string;
    name?: string;
    color?: string;
  }>;
  label?: string;
  displayMode: DisplayMode;
  consumptionType: string;
  goalPlan: GoalChartPlan | null;
}

const tooltipShellStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '0.5rem 0.75rem',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

export const GoalMilestoneTooltipDetails: React.FC<{
  milestone: GoalMilestone;
  unit: string;
}> = ({ milestone, unit }) => (
  <div>
    <p style={{ margin: 0, fontWeight: 600, color: milestone.color }}>
      {milestone.shortLabel}
    </p>
    <p style={{ margin: '0.2rem 0 0', fontWeight: 600 }}>
      {milestone.targetValue.toFixed(4)} {unit}
    </p>
  </div>
);

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  displayMode,
  consumptionType,
  goalPlan,
}) => {
  if (active && payload && payload.length && payload[0]) {
    const data = payload[0].payload;
    const milestone =
      displayMode === 'per_dish'
        ? (goalPlan?.milestones.find(
            (item) => item.deadlineMonth === data.date,
          ) ?? null)
        : null;

    const getUnit = (type: string, isPerDish: boolean): string => {
      const baseUnits: Record<string, string> = {
        kwh: 'kWh',
        m3_water: WATER_UNIT.TOTAL,
        m3_gas: 'm³',
        kg_recyclables: 'kg',
        kg_organic_waste: 'kg',
        kg_general_waste: 'kg',
        kg_co2e: 'kgCO₂e',
      };

      return isPerDish
        ? `${baseUnits[type] || 'unidad'}/plato`
        : baseUnits[type] || 'unidad';
    };

    if (milestone) {
      return (
        <div className="custom-tooltip" style={tooltipShellStyle}>
          <GoalMilestoneTooltipDetails
            milestone={milestone}
            unit={goalPlan?.unit ?? ''}
          />
        </div>
      );
    }

    const actualEntry = payload.find(
      (entry) => entry.dataKey === 'value' && typeof entry.value === 'number',
    );
    if (actualEntry && typeof actualEntry.value === 'number') {
      return (
        <div className="custom-tooltip" style={tooltipShellStyle}>
          <p
            style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}
          >
            {formatNumberEs(
              actualEntry.value,
              displayMode === 'per_dish' ? 4 : 2,
            )}{' '}
            {getUnit(consumptionType, displayMode === 'per_dish')}
          </p>
          {data.covers_count && displayMode === 'per_dish' && (
            <span
              style={{
                color: 'var(--text-secondary)',
                display: 'block',
                fontSize: '0.8rem',
                marginTop: '0.25rem',
              }}
            >
              ({data.covers_count} cubiertos)
            </span>
          )}
          {!data.has_sales_data && displayMode === 'per_dish' && (
            <p
              style={{
                margin: '0.25rem 0 0 0',
                color: 'var(--warning-600)',
                fontSize: '0.75rem',
              }}
            >
              ⚠️ Sin datos de ventas
            </p>
          )}
        </div>
      );
    }

    if (displayMode === 'per_dish' && goalPlan) {
      const planEntries = goalPlan.originalTrajectories.flatMap(
        (trajectory) => {
          const entry = payload.find(
            (item) =>
              item.dataKey === `original_${trajectory.goalId}` &&
              typeof item.value === 'number',
          );
          if (!entry || typeof entry.value !== 'number') return [];
          return [
            {
              shortLabel: trajectory.shortLabel,
              color: trajectory.color,
              value: entry.value,
            },
          ];
        },
      );
      if (planEntries.length > 0) {
        return (
          <div className="custom-tooltip" style={tooltipShellStyle}>
            {planEntries.map((entry) => (
              <p
                key={entry.shortLabel}
                style={{
                  margin: '0.15rem 0',
                  fontWeight: 600,
                  color: entry.color,
                }}
              >
                {entry.shortLabel}: {entry.value.toFixed(4)} {goalPlan.unit}
              </p>
            ))}
          </div>
        );
      }
    }
  }

  return null;
};

// ============================================================================
// Component
// ============================================================================

export const PerDishConsumptionChart: React.FC<
  PerDishConsumptionChartProps
> = ({
  data,
  consumptionType,
  title,
  loading = false,
  isRefetching = false,
  error,
  className,
  goalPlan = null,
}) => {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('per_dish');

  // Transform data for chart based on current display mode
  const chartData = useMemo(() => {
    if (data.length === 0) return [];

    return data.map((metrics) => {
      const perDishValue = metrics[PER_DISH_KEYS[consumptionType]] ?? null;
      const totalValue = metrics[TOTAL_KEYS[consumptionType]] ?? null;

      let value: number | null = null;

      if (displayMode === 'per_dish') {
        // Prefer per-dish, fallback to total if no sales data (as per requirement)
        value =
          perDishValue !== null
            ? perDishValue
            : !metrics.has_sales_data && totalValue !== null
              ? totalValue
              : null;
      } else if (displayMode === 'total') {
        value = totalValue;
      } else {
        // This should never happen with the current DisplayMode type
        value = null;
      }

      const dateStr = `${metrics.year}-${String(metrics.month).padStart(2, '0')}`;

      return {
        date: dateStr,
        value: value === null ? null : Math.round(value * 10000) / 10000,
        formatted_date: metrics.month_name,
        total_value: totalValue,
        per_dish_value: perDishValue,
        ...(metrics.covers_count !== undefined
          ? { covers_count: metrics.covers_count }
          : {}),
        has_sales_data: metrics.has_sales_data,
      };
    });
  }, [data, consumptionType, displayMode]);

  const displayData = useMemo(() => {
    if (displayMode !== 'per_dish' || !goalPlan) {
      return chartData;
    }

    const pointsByDate = new Map<string, ChartDisplayPoint>();

    chartData.forEach((point) => {
      pointsByDate.set(point.date, { ...point });
    });

    const ensurePoint = (date: string): ChartDisplayPoint => {
      const existing = pointsByDate.get(date);
      if (existing) return existing;
      const formattedDate = new Intl.DateTimeFormat('es-ES', {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      }).format(new Date(`${date}-01T00:00:00Z`));
      const created = {
        date,
        formatted_date: formattedDate,
        value: null,
      };
      pointsByDate.set(date, created);
      return created;
    };

    goalPlan.originalTrajectories.forEach((trajectory) => {
      trajectory.points.forEach((point) => {
        pointsByDate.set(point.date, {
          ...ensurePoint(point.date),
          [`original_${trajectory.goalId}`]: point.value,
        });
      });
    });

    goalPlan.requiredPace.forEach((point) => {
      pointsByDate.set(point.date, {
        ...ensurePoint(point.date),
        required_pace: point.value,
      });
    });

    goalPlan.benchmarks.forEach((benchmark) => {
      benchmark.points.forEach((point) => {
        pointsByDate.set(point.date, {
          ...ensurePoint(point.date),
          [`benchmark_${benchmark.goalId}`]: point.value,
        });
      });
    });

    return Array.from(pointsByDate.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [chartData, displayMode, goalPlan]);

  // Calculate dynamic height based on data points
  const { containerHeight, contentHeight } = useStableChartHeight({
    dataPointCount: chartData.length,
    isRefetching,
    headerOffset: 128,
  });

  // Generate unit display
  const getDisplayUnit = (): string => {
    const baseUnits: Record<string, string> = {
      kwh: 'kWh',
      m3_water: WATER_UNIT.TOTAL,
      m3_gas: 'm³',
      kg_recyclables: 'kg',
      kg_organic_waste: 'kg',
      kg_general_waste: 'kg',
      kg_co2e: 'kgCO₂e',
    };

    const baseUnit = baseUnits[consumptionType] || 'unidad';

    if (displayMode === 'per_dish') {
      return `${baseUnit}/plato`;
    } else if (displayMode === 'total') {
      return baseUnit;
    } else {
      return baseUnit; // fallback for type safety
    }
  };

  const hasActualData = chartData.some((point) => point.value !== null);
  const hasGoalData = Boolean(
    goalPlan &&
      (goalPlan.milestones.length > 0 ||
        goalPlan.requiredPace.length > 0 ||
        goalPlan.originalTrajectories.length > 0),
  );
  const shouldShowInitialLoading = loading && !hasActualData;

  if (shouldShowInitialLoading) {
    return (
      <ChartContainer className={className} $minHeight={containerHeight}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
        </ChartHeader>
        <ChartContent $minHeight={contentHeight}>
          <LoadingSpinner size="md" />
        </ChartContent>
      </ChartContainer>
    );
  }

  if (error) {
    return (
      <ChartContainer className={className} $minHeight={containerHeight}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
        </ChartHeader>
        <ChartContent $minHeight={contentHeight}>
          <ErrorMessage>{error}</ErrorMessage>
        </ChartContent>
      </ChartContainer>
    );
  }

  if (!hasActualData && !hasGoalData) {
    return (
      <ChartContainer className={className} $minHeight={containerHeight}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
          <ToggleGroup>
            <ToggleButton
              $isActive={displayMode === 'per_dish'}
              onClick={() => setDisplayMode('per_dish')}
            >
              Por plato
            </ToggleButton>
            <ToggleButton
              $isActive={displayMode === 'total'}
              onClick={() => setDisplayMode('total')}
            >
              Total
            </ToggleButton>
          </ToggleGroup>
        </ChartHeader>
        <ChartContent $minHeight={contentHeight}>
          <EmptyMessage>
            No hay datos disponibles para este período
          </EmptyMessage>
        </ChartContent>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer className={className} $minHeight={containerHeight}>
      <ChartHeader>
        <div>
          <ChartTitle>{title}</ChartTitle>
          <UnitLabel>{getDisplayUnit()}</UnitLabel>
          {hasGoalData && displayMode === 'total' && (
            <GoalVisibilityHint>
              Las metas y su ritmo necesario solo se muestran en la vista “Por
              plato”.
            </GoalVisibilityHint>
          )}
        </div>
        <ToggleGroup>
          <ToggleButton
            $isActive={displayMode === 'per_dish'}
            onClick={() => setDisplayMode('per_dish')}
            aria-label="Mostrar consumo por plato"
          >
            Por plato
          </ToggleButton>
          <ToggleButton
            $isActive={displayMode === 'total'}
            onClick={() => setDisplayMode('total')}
            aria-label="Mostrar consumo total"
          >
            Total
          </ToggleButton>
        </ToggleGroup>
      </ChartHeader>

      <ChartContent $minHeight={contentHeight}>
        <ResponsiveContainer
          width="100%"
          height={contentHeight}
          minHeight={200}
          maxHeight={520}
        >
          <LineChart
            data={displayData}
            margin={{
              top: 5,
              right: 12,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="date"
              stroke="var(--text-secondary)"
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              tickFormatter={(value: string) =>
                new Intl.DateTimeFormat('es-ES', {
                  month: 'short',
                  year: '2-digit',
                  timeZone: 'UTC',
                }).format(new Date(`${value}-01T00:00:00Z`))
              }
            />
            <YAxis
              stroke="var(--text-secondary)"
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <Tooltip
              content={
                <CustomTooltip
                  displayMode={displayMode}
                  consumptionType={consumptionType}
                  goalPlan={goalPlan}
                />
              }
            />
            <Line
              type="monotone"
              dataKey="value"
              name="Real"
              stroke="var(--brand-600)"
              strokeWidth={2}
              dot={{ fill: 'var(--brand-600)', strokeWidth: 2, r: 3 }}
              activeDot={{
                r: 6,
                stroke: 'var(--brand-600)',
                strokeWidth: 2,
                fill: 'var(--surface)',
              }}
              legendType="none"
            />
            {displayMode === 'per_dish' && goalPlan && (
              <>
                {goalPlan.originalTrajectories.map((trajectory) => (
                  <Line
                    key={`original_${trajectory.goalId}`}
                    type="linear"
                    dataKey={`original_${trajectory.goalId}`}
                    name={trajectory.shortLabel}
                    stroke={trajectory.color}
                    strokeWidth={1.75}
                    strokeDasharray="3 3"
                    connectNulls
                    dot={false}
                    legendType="none"
                  />
                ))}
                {goalPlan.benchmarks.map((benchmark) => (
                  <Line
                    key={`benchmark_${benchmark.goalId}`}
                    type="linear"
                    dataKey={`benchmark_${benchmark.goalId}`}
                    name={benchmark.shortLabel}
                    stroke={benchmark.color}
                    strokeWidth={1.5}
                    strokeDasharray="1 4"
                    connectNulls
                    dot={false}
                    legendType="none"
                    opacity={0.7}
                  />
                ))}
                {goalPlan.requiredPace.length > 0 && (
                  <Line
                    type="linear"
                    dataKey="required_pace"
                    name="Ritmo"
                    stroke="var(--brand-700)"
                    strokeWidth={2}
                    strokeDasharray="7 5"
                    connectNulls
                    dot={false}
                    activeDot={{ r: 5 }}
                    legendType="none"
                  />
                )}
                {goalPlan.milestones.map((milestone) => (
                  <ReferenceDot
                    key={milestone.goalId}
                    x={milestone.deadlineMonth}
                    y={milestone.targetValue}
                    r={6}
                    fill={
                      milestone.sequenceStatus === 'legacy_invalid'
                        ? 'var(--surface)'
                        : milestone.color
                    }
                    stroke={milestone.color}
                    strokeWidth={
                      milestone.sequenceStatus === 'legacy_invalid' ? 3 : 2
                    }
                    label={{
                      value: milestone.shortLabel,
                      position: 'top',
                      fill: milestone.color,
                      fontSize: 10,
                    }}
                  />
                ))}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </ChartContent>
    </ChartContainer>
  );
};
